import React, { useEffect, useCallback, useRef, RefObject, useState, useMemo } from "react";
import { LeftArrowIcon } from "../../icons/LeftArrowIcon";
import { DiseaseCategory } from "../DiseaseCategory";
import { DiseaseMenuSection } from "./DiseaseMenuSection";
import { DiseaseKnowledgeDetailIndex } from "./DiseaseKnowledgeDetailIndex";
import { SecondaryButton } from "../SecondaryButton";
import { getInfoAction, openDialogAction } from "../../../../../state/usecase/disease_knowledge/actions";
import { useDiseases } from "../../../../hooks/disease_knowledge/useDiseases";
import { useAppDispatch } from "../../../../../state/store";
import { DetailContent } from "./DetailContent";
import { DetailContentWatch } from "./DetailContentWatch";
import { DetailContentAssessmentCheckpoint } from "./DetailContentAssessmentCheckpoint";
import { DetailContentSuggestingService } from "./DetailContentSuggestingService";

export const DiseaseKnowledgeDetailDialog = React.memo(function DiseaseKnowledgeDetailDialog(props: Props) {
    const { diseaseId, dialogContainerRef } = props;

    const dispatch = useAppDispatch();

    const disease = useDiseases(diseaseId);

    useEffect(() => {
        dispatch(getInfoAction(diseaseId));
    }, [dispatch, diseaseId]);
    
    const [activeSection, setActiveSection] = useState<DiseaseMenuSection | null>(DiseaseMenuSection.ABSTRACT_DISEASE);

    const abstractDiseaseSectionRef = useRef<HTMLHeadingElement>(null);

    const symptomRef = useRef<HTMLHeadingElement>(null);

    const misleadingDiseaseRef = useRef<HTMLHeadingElement>(null);

    const watchRef = useRef<HTMLHeadingElement>(null);

    const assessmentCheckpointRef = useRef<HTMLHeadingElement>(null);

    const suggestingServiceRef = useRef<HTMLHeadingElement>(null);

    const adviceRef = useRef<HTMLHeadingElement>(null);

    const sectionRefs = useMemo(() => (
        [
            { section: DiseaseMenuSection.ABSTRACT_DISEASE, value: abstractDiseaseSectionRef },
            { section: DiseaseMenuSection.SYMPTOM, value: symptomRef },
            { section: DiseaseMenuSection.MISLEADING_DISEASE, value: misleadingDiseaseRef },
            { section: DiseaseMenuSection.WATCH, value: watchRef },
            { section: DiseaseMenuSection.ASSESMENT_CHECKPOINT, value: assessmentCheckpointRef },
            { section: DiseaseMenuSection.SUGGESTING_SERVICE, value: suggestingServiceRef },
            { section: DiseaseMenuSection.ADVICE, value: adviceRef },
        ]
    ), []);

    const onScroll = useCallback(() => {
        const dialogScrollHeight = dialogContainerRef.current?.scrollHeight ?? 0;
        const dialogScrollTop = dialogContainerRef.current?.scrollTop ?? 0;
        const dialogOffsetHeight = dialogContainerRef.current?.offsetHeight ?? 0;
        const dialogScrollBottom = dialogScrollTop + dialogOffsetHeight;
        if (sectionRefs.every(sectionRef => sectionRef.value.current)) {
            const sectionOffsetBottoms = sectionRefs.map(sectionRef => {
                const { section, value: ref } = sectionRef;
                const offsetTop = ref.current!.offsetTop;
                const offsetHeight = ref.current!.offsetHeight;
                const offsetBottom = offsetTop + offsetHeight;
                return { section, offsetTop, offsetBottom };
            });
            sectionOffsetBottoms.forEach(sectionOffsetBottom => {
                // スクロールエリアの上半分の場合
                if (dialogScrollTop < (dialogScrollHeight / 2)) {
                    // スクロールTopが要素のTopを越えている場合
                    if (dialogScrollTop > sectionOffsetBottom.offsetTop - 50) setActiveSection(sectionOffsetBottom.section);
                } else {
                    if (dialogScrollBottom > sectionOffsetBottom.offsetBottom - 50) setActiveSection(sectionOffsetBottom.section);
                }
            });
        }
    }, [sectionRefs, dialogContainerRef]);

    useEffect(() => {
        const current = dialogContainerRef.current;
        current?.addEventListener("scroll", onScroll);
        return () => {
            current?.removeEventListener("scroll", onScroll);
        };
    }, [dialogContainerRef, onScroll]);

    const openListDialogButton = useCallback(() => {
        dispatch(openDialogAction("list"));
    }, [dispatch]);

    // 医療情報取得中
    if (!disease) return null;

    return (
        <div className={"detail_dialog"}>
            <div className={"container"}>

                <button className={"listDialogButton"} onClick={openListDialogButton}>
                    <LeftArrowIcon />
                    <span>医療知識一覧画面</span>
                </button>

                <h2 className={"title"}>
                    {disease.name}
                    <div className={"diseaseCategory"}>
                        <DiseaseCategory categoryId={disease.category.id} />
                        <span>{disease.category.name}</span>
                    </div>
                </h2>

                <div className={"columns"}>
                    <div className={"content"}>
                        <DetailContent
                            menuSection={DiseaseMenuSection.ABSTRACT_DISEASE}
                            elementRef={abstractDiseaseSectionRef}
                            itemName={"病気の概要"}
                            itemContents={disease.abstractDiseases}
                        />
                        <DetailContent
                            menuSection={DiseaseMenuSection.SYMPTOM}
                            elementRef={symptomRef}
                            itemName={"主な症状"}
                            itemContents={disease.symptoms}
                        />
                        <DetailContent
                            menuSection={DiseaseMenuSection.SYMPTOM}
                            elementRef={symptomRef}
                            itemName={"主な症状"}
                            itemContents={disease.symptoms}
                        />
                        <DetailContent
                            menuSection={DiseaseMenuSection.MISLEADING_DISEASE}
                            elementRef={misleadingDiseaseRef}
                            itemName={"症状に変化があった場合他に疑った方が良い病気"}
                            itemContents={disease.misleadingDiseases}
                        />
                        <DetailContentWatch disease={disease} watchRef={watchRef} />
                        <DetailContentAssessmentCheckpoint assessmentCheckpointRef={assessmentCheckpointRef} disease={disease} />
                        <DetailContentSuggestingService disease={disease} suggestingServiceRef={suggestingServiceRef} />
                        <DetailContent
                            menuSection={DiseaseMenuSection.ADVICE}
                            elementRef={adviceRef}
                            itemName={"熟練ケアマネからのワンポイントアドバイス"}
                            itemContents={disease.advices}
                        />
                    </div>
                    <div className={"index"}>
                        <div className={"fixed"}>
                            <DiseaseKnowledgeDetailIndex activeSection={activeSection}/>
                        </div>
                        {/*
                        * indexをfixedにした際、要素サイズが無くなりcontentが広がってしまう。
                        * それを防ぐため、indexと同等のサイズのものを配置することによってcontentが広がるのを防ぐ。
                        * もちろん目に見えてはいけないため非表示。
                        */}
                        <div className={"hidden"}>
                            <DiseaseKnowledgeDetailIndex activeSection={DiseaseMenuSection.ABSTRACT_DISEASE}/>
                        </div>
                    </div>
                </div>
                <div className={"footer"}>
                    <SecondaryButton width={256} onClick={openListDialogButton}>医療知識一覧に戻る</SecondaryButton>
                </div>
            </div>
        </div>
    );
});

type Props = {
    diseaseId: string,
    dialogContainerRef: RefObject<HTMLDivElement>,
};
